/**
 * Centralized logging infrastructure
 *
 * Provides consistent logging across the application with:
 * - localStorage gating: debug/info logs only output when 'ahoy:debug' is set
 * - Ring buffer: last 500 log entries stored in memory for later inspection
 * - DevTools integration: window.__ahoy.enable/disable/logs for console access
 * - Error/warn always output to console regardless of gating
 *
 * Usage:
 *   import { logger } from '@/lib/logger';
 *   logger.debug('Loading data', { id: 123 });
 *
 *   // In browser console:
 *   window.__ahoy.enable()   // Start showing debug/info in console
 *   window.__ahoy.disable()  // Silence debug/info again
 *   window.__ahoy.logs()     // View buffered log entries
 */

import { isDevelopment } from '@/lib/config';
import type { AppError } from '@/lib/errors';
import type { PerfDevtools } from '@/lib/perf/perfTrace';

const STORAGE_KEY = 'ahoy:debug';
const BUFFER_SIZE = 500;

/**
 * Log levels
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * Log context - additional metadata
 */
export interface LogContext {
  [key: string]: unknown;
}

/**
 * A single buffered log entry
 */
export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: LogContext;
}

/**
 * Check if console output is enabled via localStorage
 */
function isConsoleEnabled(): boolean {
  try {
    return !!localStorage.getItem(STORAGE_KEY);
  } catch {
    // localStorage may be unavailable (SSR, privacy mode)
    return false;
  }
}

/**
 * Logger interface - defines all methods available on Logger instances
 */
export interface LoggerInterface {
  debug(message: string, context?: LogContext): void;
  info(message: string, context?: LogContext): void;
  warn(message: string, context?: LogContext): void;
  error(message: string, error?: Error | AppError, context?: LogContext): void;
  apiCall(method: string, url: string, context?: LogContext): void;
  apiResponse(
    method: string,
    url: string,
    status: number,
    context?: LogContext
  ): void;
  apiError(
    method: string,
    url: string,
    error: Error | AppError,
    context?: LogContext
  ): void;
  dump(): LogEntry[];
  enable(): void;
  disable(): void;
  child(defaultContext: LogContext): LoggerInterface;
}

/**
 * Logger class with ring buffer and localStorage gating
 */
class Logger implements LoggerInterface {
  private isDev: boolean;
  private buffer: LogEntry[] = [];
  private bufferIndex = 0;
  private bufferFull = false;

  constructor() {
    this.isDev = isDevelopment();
  }

  /**
   * Add an entry to the ring buffer
   */
  private addToBuffer(
    level: LogLevel,
    message: string,
    context?: LogContext
  ): void {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
    };

    if (this.buffer.length < BUFFER_SIZE) {
      this.buffer.push(entry);
    } else {
      this.buffer[this.bufferIndex] = entry;
      this.bufferFull = true;
    }
    this.bufferIndex = (this.bufferIndex + 1) % BUFFER_SIZE;
  }

  /**
   * Log a debug message (gated by localStorage)
   */
  debug(message: string, context?: LogContext): void {
    this.addToBuffer('debug', message, context);
    if (this.isDev && isConsoleEnabled()) {
      console.debug(`[DEBUG] ${message}`, context || '');
    }
  }

  /**
   * Log an info message (gated by localStorage)
   */
  info(message: string, context?: LogContext): void {
    this.addToBuffer('info', message, context);
    if (this.isDev && isConsoleEnabled()) {
      console.info(`[INFO] ${message}`, context || '');
    }
  }

  /**
   * Log a warning (always outputs to console)
   */
  warn(message: string, context?: LogContext): void {
    this.addToBuffer('warn', message, context);
    if (this.isDev) {
      console.warn(`[WARN] ${message}`, context || '');
    } else {
      this.logToConsoleInProduction('warning', message, context);
    }
  }

  /**
   * Log an error (always outputs to console)
   */
  error(message: string, error?: Error | AppError, context?: LogContext): void {
    this.addToBuffer('error', message, { error, ...context });
    if (this.isDev) {
      console.error(`[ERROR] ${message}`, { error, ...context });
    } else {
      this.logToConsoleInProduction('error', message, { error, ...context });
    }
  }

  /**
   * Log an API call (for debugging)
   */
  apiCall(method: string, url: string, context?: LogContext): void {
    this.debug(`API ${method} ${url}`, context);
  }

  /**
   * Log an API response (for debugging)
   */
  apiResponse(
    method: string,
    url: string,
    status: number,
    context?: LogContext
  ): void {
    this.debug(`API ${method} ${url} -> ${status}`, context);
  }

  /**
   * Log an API error
   */
  apiError(
    method: string,
    url: string,
    error: Error | AppError,
    context?: LogContext
  ): void {
    this.error(`API ${method} ${url} failed`, error, context);
  }

  /**
   * Return all buffered log entries in chronological order
   */
  dump(): LogEntry[] {
    if (!this.bufferFull) {
      return [...this.buffer];
    }
    // Ring buffer wrapped: entries from bufferIndex..end are oldest
    return [
      ...this.buffer.slice(this.bufferIndex),
      ...this.buffer.slice(0, this.bufferIndex),
    ];
  }

  /**
   * Enable console output for debug/info logs
   * Note: This method always outputs a console message, even if logging is disabled.
   */
  enable(): void {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
      console.info('[ahoy] Debug logging enabled. Reload to see all logs.');
    } catch {
      console.warn('[ahoy] Could not set localStorage key');
    }
  }

  /**
   * Disable console output for debug/info logs
   * Note: This method always outputs a console message, even if logging is disabled.
   */
  disable(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
      console.info('[ahoy] Debug logging disabled.');
    } catch {
      console.warn('[ahoy] Could not remove localStorage key');
    }
  }

  /**
   * Log to console in production
   * TODO: Replace with actual Sentry integration for error tracking
   */
  private logToConsoleInProduction(
    level: 'warning' | 'error',
    message: string,
    context?: LogContext
  ): void {
    if (level === 'error') {
      console.error(message, context);
    } else {
      console.warn(message, context);
    }
  }

  /**
   * Create a child logger with additional context
   */
  child(defaultContext: LogContext): LoggerInterface {
    return new ChildLogger(this, defaultContext);
  }
}

/**
 * Child logger class that merges default context with provided context
 */
class ChildLogger implements LoggerInterface {
  constructor(
    private parent: LoggerInterface,
    private defaultContext: LogContext
  ) {}

  debug(message: string, context?: LogContext): void {
    this.parent.debug(message, { ...this.defaultContext, ...context });
  }

  info(message: string, context?: LogContext): void {
    this.parent.info(message, { ...this.defaultContext, ...context });
  }

  warn(message: string, context?: LogContext): void {
    this.parent.warn(message, { ...this.defaultContext, ...context });
  }

  error(message: string, error?: Error | AppError, context?: LogContext): void {
    this.parent.error(message, error, { ...this.defaultContext, ...context });
  }

  apiCall(method: string, url: string, context?: LogContext): void {
    this.parent.apiCall(method, url, { ...this.defaultContext, ...context });
  }

  apiResponse(
    method: string,
    url: string,
    status: number,
    context?: LogContext
  ): void {
    this.parent.apiResponse(method, url, status, {
      ...this.defaultContext,
      ...context,
    });
  }

  apiError(
    method: string,
    url: string,
    error: Error | AppError,
    context?: LogContext
  ): void {
    this.parent.apiError(method, url, error, {
      ...this.defaultContext,
      ...context,
    });
  }

  dump(): LogEntry[] {
    return this.parent.dump();
  }

  enable(): void {
    this.parent.enable();
  }

  disable(): void {
    this.parent.disable();
  }

  child(childContext: LogContext): LoggerInterface {
    return new ChildLogger(this.parent, {
      ...this.defaultContext,
      ...childContext,
    });
  }
}

/**
 * Default logger instance
 */
export const logger = new Logger();

/**
 * Create a logger with default context
 * Useful for component-specific or feature-specific logging
 */
export function createLogger(defaultContext: LogContext): LoggerInterface {
  return logger.child(defaultContext);
}

// Expose devtools interface on window. The log helpers are dev-only (set
// below); `perf` is installed in every environment by lib/perf/perfTrace.ts,
// which is why each member is optional.
declare global {
  interface Window {
    __ahoy?: {
      logs?: () => LogEntry[];
      enable?: () => void;
      disable?: () => void;
      perf?: PerfDevtools;
    };
  }
}

if (typeof window !== 'undefined' && isDevelopment()) {
  window.__ahoy = {
    logs: () => logger.dump(),
    enable: () => logger.enable(),
    disable: () => logger.disable(),
  };
}
